import { useRef, useState } from 'react';

import { Icon } from '~/components/Icon';
import { IconButton } from '~/components/IconButton';
import { cn } from '~/lib/cn';

interface ArtistSearchBarProps {
  initialSearchText?: string;
  placeholder: string;
  onSubmit: (searchValue: string) => void;
  onClear: () => void;
}

function ArtistSearchBar({
  initialSearchText,
  placeholder,
  onSubmit,
  onClear,
}: ArtistSearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>(initialSearchText ?? '');

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleClearButtonClick = () => {
    inputRef.current?.focus();
    setInputValue('');
    onClear();
  };

  return (
    <form
      className="relative flex grow items-center"
      role="search"
      aria-label="Search artists by name"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(inputValue);
      }}
    >
      <Icon
        className={cn(
          'fa-light fa-search fa-sm pointer-events-none absolute px-sm pl-md',
        )}
        type="search"
        variant="light"
        size="sm"
        aria-hidden
      />
      <input
        ref={inputRef}
        type="text"
        name="search"
        className={cn(
          'grow place-self-stretch rounded-sm py-sm pl-2xl text-body-sm italic leading-8 duration-300 ease-out',
          'outline outline-1 bg-emphasis-default outline-emphasis-subtle',
          'hover:bg-emphasis-strong hover:outline-emphasis-stronger focus:placeholder:text-default',
        )}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        aria-label="Search input for artist name"
      />
      {inputValue ? (
        <IconButton
          type="button"
          iconType="circle-xmark"
          size="small"
          className="absolute right-sm duration-300 ease-out text-subtle hover:text-icon-strong"
          onClick={handleClearButtonClick}
          aria-label="Clear search input"
        />
      ) : null}
    </form>
  );
}

ArtistSearchBar.displayName = 'ArtistSearchBar';

export { ArtistSearchBar };
